/* ==========================================================================
   Ecoline — plp-filter.js (PLP owner)
   Client-side facet filtering for sections/main-collection.liquid
   (BUILD-DECISIONS #7: filters stay client-side, restyled into the v2
   sidebar/drawer, plus URL-param deep links).

   Reads data-room/data-price/data-finish off each [data-plp-card] <li>
   (rendered server-side by main-collection.liquid) and shows/hides cards
   against the checkbox facets in snippets/plp-filter-panel.liquid. The
   panel renders TWICE per page (desktop sidebar + mobile drawer,
   [data-drawer="filter"] opened/closed by the generic Ecoline.drawer in
   assets/app.js — this file only owns filtering, not drawer chrome), so
   every facet input has a twin: checked state is mirrored across inputs
   sharing data-facet + value, and readState() de-duplicates values.

   Matching rules: multiple checked values within one facet = OR, across
   facets = AND. A card's data-<facet> attribute is a whitespace-separated
   token list (data-room="living-room bedroom"); single-value facets are a
   one-token list, so the same membership test covers both. Facet option
   counts + disabled state recompute on every change (cheap at ~11 SKUs).

   FILTER DEEP-LINK CONTRACT (shared with home room-tiles / SEO chips):
   /collections/<handle>?room=<token>&price=<bucket>&finish=<name>&sort=<sort>
     room   living-room|bedroom|study-home-office|kids-room
     price  under-1000|1000-2500|2500-5000|over-5000 (bucket slugs, NOT
            paise — DESIGN's four price bands)
     finish White|Walnut|Dual-tone
   Multiple values within a facet join with commas (?room=bedroom,kids-room).
   On load the params are read and pre-applied; on every change the URL is
   mirrored back via history.replaceState so filtered views are shareable
   and survive back/forward. ?sort= is an alias for Shopify's native
   ?sort_by= — when it arrives, we navigate once to sort_by so the server
   actually re-orders the grid (the sort <select> itself is wired by an
   inline script in main-collection.liquid and untouched here).

   Applied-filter chips render into every [data-filter-chips] container
   (sidebar, drawer, mobile row); clicking a chip removes that one value.
   Decorative grid inserts ([data-plp-decoration]: 17mm banner, end-cap
   pill) hide whenever a filter is active so a filtered grid doesn't claim
   "that's the whole catalogue".
   ========================================================================== */
(function () {
  'use strict';

  /* Facets that exist in v2 and mirror to the URL (the v1 material/size/
     availability/category facets are gone). */
  var FACETS = ['room', 'price', 'finish'];

  /* Module-level registry + ONE popstate/pageshow listener pair. Binding
     these window listeners per-[data-plp-root] would stack a fresh pair on
     every shopify:section:load re-render, with the stale handlers filtering
     detached card lists forever; the shared handlers walk the registry and
     prune disconnected roots instead. */
  var registry = [];

  function restoreAllFromUrl() {
    for (var i = registry.length - 1; i >= 0; i--) {
      if (!registry[i].root.isConnected) {
        registry.splice(i, 1);
        continue;
      }
      registry[i].applyFromUrl();
      registry[i].apply(false);
    }
  }

  window.addEventListener('popstate', restoreAllFromUrl);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) restoreAllFromUrl();
  });

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  /* Current selection: { facetName: [value, ...] } — only facets with at
     least one checked box are included; values de-duplicated because every
     input exists twice (sidebar + drawer). */
  function readState(inputs) {
    var state = {};
    inputs.forEach(function (input) {
      if (!input.checked) return;
      var facet = input.getAttribute('data-facet');
      if (!state[facet]) state[facet] = [];
      if (state[facet].indexOf(input.value) === -1) state[facet].push(input.value);
    });
    return state;
  }

  /* A card's data-<facet> value, split into whitespace-separated tokens. */
  function cardTokens(card, facet) {
    var raw = card.getAttribute('data-' + facet) || '';
    return raw.split(/\s+/).filter(Boolean);
  }

  /* True when `card` satisfies every facet in `state`, optionally ignoring
     one facet (used for "how many would match if this facet's own
     selection didn't count" option counts). Within a facet ANY selected
     value may match (OR); across facets all must (AND). */
  function cardMatches(card, state, skipFacet) {
    for (var facet in state) {
      if (facet === skipFacet) continue;
      var values = state[facet];
      if (!values || !values.length) continue;
      var tokens = cardTokens(card, facet);
      var matched = values.some(function (v) { return tokens.indexOf(v) > -1; });
      if (!matched) return false;
    }
    return true;
  }

  function initPlp(root) {
    if (!root || root.elPlpFilterInit) return;
    root.elPlpFilterInit = true;

    var cards = toArray(root.querySelectorAll('[data-plp-card]'));
    var inputs = toArray(root.querySelectorAll('[data-filter-input]'));
    if (!cards.length || !inputs.length) return;

    var countLabel = root.querySelector('[data-plp-count]');
    var defaultCountText = countLabel ? countLabel.textContent : '';
    var emptyState = root.querySelector('[data-plp-filter-empty]');
    var grid = root.querySelector('[data-plp-grid]');
    var resultCountEls = toArray(root.querySelectorAll('[data-filter-result-count]'));
    var activeBadges = toArray(root.querySelectorAll('[data-filter-active-count]'));
    var clearBtns = toArray(root.querySelectorAll('[data-filter-clear]'));
    var chipBoxes = toArray(root.querySelectorAll('[data-filter-chips]'));
    var decorations = toArray(root.querySelectorAll('[data-plp-decoration]'));
    var sortSelect = root.querySelector('[data-sort-select]');

    /* Deep-link alias: ?sort= → native ?sort_by= (one navigation; the
       server re-orders the grid). Skipped when the page is already sorted
       that way or the value isn't a known option. */
    (function () {
      var params = new URLSearchParams(window.location.search);
      var sortVal = params.get('sort');
      if (!sortVal || !sortSelect) return;
      var known = toArray(sortSelect.options).some(function (opt) { return opt.value === sortVal; });
      if (!known || sortVal === sortSelect.value) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortVal);
      url.searchParams.delete('sort');
      window.location.replace(url.href);
    })();

    function labelFor(facet, value) {
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].getAttribute('data-facet') === facet && inputs[i].value === value) {
          return inputs[i].getAttribute('data-label') || value;
        }
      }
      return value;
    }

    function setFacetValue(facet, value, checked) {
      inputs.forEach(function (input) {
        if (input.getAttribute('data-facet') === facet && input.value === value) {
          input.checked = checked;
        }
      });
    }

    /* Check inputs from the URL's facet params (deep links / back-forward). */
    function applyFromUrl() {
      var params = new URLSearchParams(window.location.search);
      inputs.forEach(function (input) {
        var facet = input.getAttribute('data-facet');
        if (FACETS.indexOf(facet) === -1) return;
        var raw = params.get(facet);
        var values = raw ? raw.split(',') : [];
        input.checked = values.indexOf(input.value) > -1;
      });
    }

    /* Mirror the selection back into the URL (replaceState — shareable,
       no history spam; sort_by etc. are preserved untouched). */
    function writeUrl(state) {
      if (!(window.history && window.history.replaceState)) return;
      var url = new URL(window.location.href);
      FACETS.forEach(function (facet) {
        var values = state[facet];
        if (values && values.length) url.searchParams.set(facet, values.join(','));
        else url.searchParams.delete(facet);
      });
      var next = url.pathname + url.search + url.hash;
      var current = window.location.pathname + window.location.search + window.location.hash;
      if (next !== current) window.history.replaceState(null, '', next);
    }

    function renderChips(state) {
      chipBoxes.forEach(function (box) {
        box.textContent = '';
        var any = false;
        FACETS.forEach(function (facet) {
          (state[facet] || []).forEach(function (value) {
            any = true;
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'el-plp__applied-chip';
            chip.setAttribute('aria-label', 'Remove filter: ' + labelFor(facet, value));
            var text = document.createElement('span');
            text.textContent = labelFor(facet, value);
            var x = document.createElement('span');
            x.className = 'el-plp__applied-chip-x';
            x.setAttribute('aria-hidden', 'true');
            x.textContent = '✕';
            chip.appendChild(text);
            chip.appendChild(x);
            chip.addEventListener('click', function () {
              setFacetValue(facet, value, false);
              apply(true);
            });
            box.appendChild(chip);
          });
        });
        box.hidden = !any;
      });
    }

    function apply(pushUrl) {
      var state = readState(inputs);
      var visible = 0;
      cards.forEach(function (card) {
        var show = cardMatches(card, state, null);
        card.hidden = !show;
        if (show) visible += 1;
      });

      /* Live per-option counts + disabled state: for each checkbox, count
         cards that would show if every OTHER facet's current selection
         still applied (never disable a box the shopper already checked —
         they need to be able to uncheck it again). */
      inputs.forEach(function (input) {
        var facet = input.getAttribute('data-facet');
        var value = input.value;
        var count = 0;
        cards.forEach(function (card) {
          if (cardTokens(card, facet).indexOf(value) === -1) return;
          if (cardMatches(card, state, facet)) count += 1;
        });
        var option = input.closest('[data-filter-option]');
        var countEl = option && option.querySelector('[data-filter-count]');
        if (countEl) countEl.textContent = '(' + count + ')';
        var disable = count === 0 && !input.checked;
        input.disabled = disable;
        if (option) option.classList.toggle('is-disabled', disable);
      });

      resultCountEls.forEach(function (el) { el.textContent = String(visible); });

      var activeCount = 0;
      for (var f in state) activeCount += state[f].length;
      activeBadges.forEach(function (badge) {
        if (activeCount > 0) {
          badge.textContent = '(' + activeCount + ')';
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      });

      if (countLabel) {
        countLabel.textContent = activeCount > 0
          ? '(' + visible + ' of ' + cards.length + ' products)'
          : defaultCountText;
      }

      if (emptyState) emptyState.hidden = visible !== 0;
      if (grid) grid.classList.toggle('is-filter-empty', visible === 0);

      /* Decorative inserts (17mm banner, end-cap pill) claim things like
         "that's the whole catalogue" — hide them while filtering. */
      decorations.forEach(function (el) { el.hidden = activeCount > 0; });

      renderChips(state);
      if (pushUrl) writeUrl(state);
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        /* Mirror to the twin input in the other panel copy, then apply. */
        setFacetValue(input.getAttribute('data-facet'), input.value, input.checked);
        apply(true);
      });
    });

    clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        inputs.forEach(function (input) { input.checked = false; });
        apply(true);
      });
    });

    /* Back/forward and bfcache restores re-read the URL as truth via the
       shared module-scope popstate/pageshow listeners (see registry). */
    registry.push({ root: root, applyFromUrl: applyFromUrl, apply: apply });

    /* First paint: pre-apply any deep-linked facets, no URL rewrite. */
    applyFromUrl();
    apply(false);
  }

  function boot(scope) {
    toArray((scope || document).querySelectorAll('[data-plp-root]')).forEach(initPlp);
  }

  ready(function () { boot(document); });

  /* theme editor: re-init when the section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) boot(e.target);
  });
})();
